/**
 * Fernando frontend trace module.
 *
 * Provides lightweight, zero-dependency observability for the Fernando
 * account's session initialization path. All trace events are:
 *   1. Logged to the browser console with a [FernandoTrace] prefix
 *   2. Written to window.__FERNANDO_TRACE__ for DevTools inspection
 *
 * For Fernando strict-mode accounts, session init failures surface
 * FERN-FE-* error codes rather than a generic spinner / silent hang.
 */

import { MIGRATED_ACCOUNT_IDS } from './constants';

// ─────────────────────────────────────────────────────────────────────────────
// Window type augmentation
// ─────────────────────────────────────────────────────────────────────────────

declare global {
  interface Window {
    __FERNANDO_TRACE__?: FernandoTraceState;
  }
}

export interface FernandoTraceEvent {
  stage: string;
  data: unknown;
  ts: number;
}

export interface FernandoTraceState {
  /** True once strict mode is confirmed for this browser session. */
  strictMode: boolean;
  /** Ordered log of all trace events emitted so far. */
  events: FernandoTraceEvent[];
}

// ─────────────────────────────────────────────────────────────────────────────
// Frontend error codes  (complement backend FERN-VM-* / FERN-ROUTE-* codes)
// ─────────────────────────────────────────────────────────────────────────────

export const FERN_FE_CODES = {
  /** Session initialization threw or returned no session object. */
  SESSION_INIT_FAILED: 'FERN-FE-001',
  /** Migration gate returned isMigrated=false for a known Fernando account. */
  MIGRATION_GATE_REJECTED: 'FERN-FE-002',
  /** handleSend was called but prest0nSessionRef.session was still null. */
  SESSION_NOT_READY_ON_SEND: 'FERN-FE-003',
  /** createMessageHandler reached the legacy-orchestrator fallback branch. */
  LEGACY_FALLBACK: 'FERN-FE-004',
} as const;

export type FernFECode = (typeof FERN_FE_CODES)[keyof typeof FERN_FE_CODES];

// ─────────────────────────────────────────────────────────────────────────────
// Strict-mode detection
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Returns true when the given accountId (or auth UID) is subject to
 * Fernando strict-mode rules: no silent VM-to-shared-worker fallback,
 * and all failures surface a structured FERN-FE-* code.
 */
export function isFernandoStrictMode(accountId: string | null | undefined): boolean {
  if (!accountId) return false;
  return MIGRATED_ACCOUNT_IDS.has(accountId);
}

// ─────────────────────────────────────────────────────────────────────────────
// Trace pub/sub (for reactive UI)
// ─────────────────────────────────────────────────────────────────────────────

type TraceSubscriber = (state: FernandoTraceState) => void;
const _subscribers: Set<TraceSubscriber> = new Set();

/**
 * Subscribe to trace state updates.
 * Returns an unsubscribe function (call in useEffect cleanup).
 */
export function subscribeToFernandoTrace(fn: TraceSubscriber): () => void {
  _subscribers.add(fn);
  // Immediately emit current state if already populated
  if (typeof window !== 'undefined' && window.__FERNANDO_TRACE__) {
    fn(window.__FERNANDO_TRACE__);
  }
  return () => { _subscribers.delete(fn); };
}

// ─────────────────────────────────────────────────────────────────────────────
// Trace emitter
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Emit a single trace event.
 *
 * @param stage   Short name for the trace point (e.g. 'AUTH_RESOLVED').
 * @param data    Arbitrary payload (serializable preferred).
 * @param strict  Pass true once the account is confirmed Fernando strict-mode.
 */
export function fernandoTrace(
  stage: string,
  data: unknown,
  strict = false
): void {
  if (typeof window === 'undefined') return;

  if (!window.__FERNANDO_TRACE__) {
    window.__FERNANDO_TRACE__ = { strictMode: false, events: [] };
  }
  if (strict) {
    window.__FERNANDO_TRACE__.strictMode = true;
  }
  window.__FERNANDO_TRACE__.events.push({ stage, data, ts: Date.now() });

  // Keep last 50 events to avoid unbounded growth
  if (window.__FERNANDO_TRACE__.events.length > 50) {
    window.__FERNANDO_TRACE__.events = window.__FERNANDO_TRACE__.events.slice(-50);
  }

  // Notify reactive subscribers with a snapshot copy
  const snapshot: FernandoTraceState = {
    strictMode: window.__FERNANDO_TRACE__.strictMode,
    events: [...window.__FERNANDO_TRACE__.events],
  };
  _subscribers.forEach((fn) => fn(snapshot));
}
